/**
 * Centralized notification → route resolver.
 * Uses `notification.data` (booking_id, pool_id, suggestion_id, note_id, mns_id, …)
 * AND the caller's role to deep-link to the precise target.
 *
 * Falls back to a sensible role-based default if no specific data is attached.
 */
export function notificationTarget(notif, role) {
  const kind = notif?.kind || "";
  const d = notif?.data || {};
  const fallback = role === "mns" ? "/mns/dashboard"
                 : role === "admin" ? "/admin"
                 : "/dashboard";

  switch (kind) {
    // ---------- Messaging ----------
    case "new_message": {
      // Prefer the conversation with the sender (data.from_user_id) when present
      if (d.from_user_id) return `/messages/${d.from_user_id}`;
      if (d.partner_id) return `/messages/${d.partner_id}`;
      return "/messages";
    }

    // ---------- Bookings (client + MNS sides) ----------
    case "new_booking":          // MNS receives a booking
    case "booking_confirmed":    // Client receives confirmation
    case "booking_reminder_24h": // J-1 reminder
    case "payment_received": {
      if (role === "mns") return d.booking_id
        ? `/mns/dashboard?tab=bookings&booking=${d.booking_id}`
        : "/mns/dashboard?tab=bookings";
      return d.booking_id ? `/dashboard?booking=${d.booking_id}` : "/dashboard";
    }

    case "new_slot": {
      // Client got a new available slot from a favorited MNS
      if (d.mns_id) return `/mns/${d.mns_id}`;
      return "/dashboard";
    }

    // ---------- Reviews & feedback ----------
    case "review_received": {
      if (d.review_id || d.booking_id) return "/mns/dashboard?tab=reviews";
      return "/mns/dashboard?tab=reviews";
    }

    // ---------- Carnet pédagogique (client side) ----------
    case "carnet_updated": {
      if (d.note_id) return `/carnet#note-${d.note_id}`;
      return "/carnet";
    }

    // ---------- Diplomas ----------
    case "diploma_status":
      return "/profile?tab=diplomas";

    // ---------- Wallet / statements / boost / subscription ----------
    case "statement_ready":
      return "/mns/dashboard?tab=accounting";
    case "boost_active":
      return "/profile?tab=subscription";
    case "subscription_status":
      return "/profile?tab=subscription";

    // ---------- Presence / no-show reminder for the MNS ----------
    case "presence_reminder":
      return d.booking_id
        ? `/mns/dashboard?tab=bookings&booking=${d.booking_id}`
        : "/mns/dashboard?tab=bookings";

    // ---------- Admin queue ----------
    case "admin_review_posted":
      return "/admin?tab=reviews";
    case "admin_doc_pending":
      return d.diploma_id
        ? `/admin?tab=diplomas&doc=${d.diploma_id}`
        : "/admin?tab=diplomas";
    case "admin_contact_received":
      return d.contact_id
        ? `/admin?tab=contact&id=${d.contact_id}`
        : "/admin?tab=contact";
    case "admin_statement_ready":
      return "/admin?tab=stats";
    case "admin_feedback_posted":
      return "/admin?tab=reviews";
    case "admin_report_received":
      return d.report_id
        ? `/admin?tab=reports&report=${d.report_id}`
        : "/admin?tab=reports";
    case "admin_pool_suggestion":
      return d.suggestion_id
        ? `/admin?tab=poolsug&suggestion=${d.suggestion_id}`
        : "/admin?tab=poolsug";

    default:
      // Last-resort: if the payload mentions a booking_id, still deep-link
      if (d.booking_id) {
        return role === "mns"
          ? `/mns/dashboard?tab=bookings&booking=${d.booking_id}`
          : `/dashboard?booking=${d.booking_id}`;
      }
      if (d.pool_id) return `/pool/${d.pool_id}`;
      if (d.mns_id) return `/mns/${d.mns_id}`;
      return fallback;
  }
}
